// Mirrors the formats the media optimization backend supports (GIF/HEIC/HEIF are
// converted to PNG server-side).
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
const VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'avi', 'mkv', 'webm'];

export const MEDIA_URL_ERROR =
  'Please enter a valid image or video URL (e.g. https://example.com/photo.jpg or .mp4)';

// http(s) URL whose path ends in a supported media extension. The extension may
// be followed by a query string and/or hash, e.g. `https://image.png?size=1000`.
// `[^\s?#]+` keeps the extension from being matched inside the query/hash and
// rejects whitespace.
const MEDIA_URL_RE = new RegExp(
  `^https?:\\/\\/[^\\s?#]+\\.(?:${[...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS].join(
    '|'
  )})(?:[?#]\\S*)?$`,
  'i'
);

export const isValidMediaUrl = (url) => MEDIA_URL_RE.test(String(url ?? '').trim());

export const MEDIA_URL_UNREACHABLE_ERROR =
  "We couldn't load an image or video from this URL. Check that the link is correct and publicly accessible.";

const URL_CHECK_TIMEOUT_MS = 10000;
const BROWSER_UNDECODABLE_URL_RE = /\.(?:heic|heif)(?:[?#]|$)/i;
const VIDEO_URL_EXT_RE = new RegExp(`\\.(?:${VIDEO_EXTENSIONS.join('|')})(?:[?#]|$)`, 'i');

/** True when the URL's extension is a video format. */
export const isVideoMediaUrl = (url) => VIDEO_URL_EXT_RE.test(String(url ?? ''));

/**
 * Best-effort check for URLs the browser can't decode as media (HEIC/HEIF).
 *  1. fetch() the URL and read status/content-type (needs the host's CORS headers).
 *  2. If that throws it's either a CORS block or a dead host (DNS/connection failure).
 *     A `no-cors` fetch tells them apart: it still resolves (opaque) when the server
 *     answered, and only rejects when nothing could be reached.
 * Anything inconclusive (CORS-blocked, timeout) resolves true; the backend remains
 * the final validator.
 * @returns {Promise<boolean>} false for a 4xx/5xx, an HTML response or an unreachable host
 */
const probeUrlResponse = async (url) => {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, URL_CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      signal: controller.signal,
      referrerPolicy: 'no-referrer',
    });
    controller.abort(); // headers are enough; don't download the body
    if (!res.ok) return false;
    return !/text\/html/i.test(res.headers.get('content-type') || '');
  } catch {
    if (timedOut) return true;
    try {
      await fetch(url, {
        mode: 'no-cors',
        signal: controller.signal,
        referrerPolicy: 'no-referrer',
      });
      return true; // reachable, just not readable cross-origin
    } catch {
      return timedOut; // nothing answered (e.g. DNS failure)
    }
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Confirm a pasted URL actually loads as an image/video (a 404, an HTML page
 * served at a .jpg path, etc. fail). Loads it through <img>/<video> rather than
 * fetch() so it works cross-origin without CORS headers. Resolves true when the
 * media loads, false on a load error. A timeout resolves true: a slow host
 * isn't proof the URL is bad, and the backend still validates it.
 * @param {string} url
 * @returns {Promise<boolean>}
 */
export const checkMediaUrlLoads = (url) =>
  new Promise((resolve) => {
    const target = String(url ?? '').trim();
    // Browsers (other than Safari) can't decode HEIC/HEIF, so <img> can't tell us anything.
    if (BROWSER_UNDECODABLE_URL_RE.test(target)) {
      probeUrlResponse(target).then(resolve);
      return;
    }
    const isVideo = VIDEO_URL_EXT_RE.test(target);
    const el = document.createElement(isVideo ? 'video' : 'img');

    let timer;
    const done = (ok) => {
      clearTimeout(timer);
      el.onload = null;
      el.onerror = null;
      el.onloadedmetadata = null;
      // Drop the source so the browser stops downloading.
      el.removeAttribute('src');
      if (isVideo) el.load();
      resolve(ok);
    };

    timer = setTimeout(() => done(true), URL_CHECK_TIMEOUT_MS);
    el.onerror = () => done(false);
    if (isVideo) {
      el.preload = 'metadata';
      el.muted = true;
      el.onloadedmetadata = () => done(true);
    } else {
      el.referrerPolicy = 'no-referrer'; // hotlink-protected hosts often block our origin
      el.onload = () => done(true);
    }
    el.src = target;
  });

// Picker filter for device uploads — same extensions the URL validator accepts.
export const MEDIA_FILE_ACCEPT = [...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS]
  .map((ext) => `.${ext}`)
  .join(',');

export const MEDIA_FILE_HINT = [...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS]
  .map((ext) => ext.toUpperCase())
  .join(', ');

export const MEDIA_FILE_ERROR = `Unsupported file. Please upload an image (${IMAGE_EXTENSIONS.map(
  (e) => e.toUpperCase()
).join(', ')}) or video (${VIDEO_EXTENSIONS.map((e) => e.toUpperCase()).join(', ')}).`;
// Device uploads are base64-encoded in the browser and sent through the API with a
// 2 min timeout and a 1 GB PHP memory limit, so anything above ~250-300 MB tends to fail.
export const MAX_MEDIA_FILE_SIZE = 250 * 1024 * 1024;
export const MEDIA_FILE_TOO_LARGE_ERROR = `This file is too large. The maximum size is ${MAX_MEDIA_FILE_SIZE /
  (1024 * 1024)} MB.`;
export const MEDIA_FILE_EMPTY_ERROR = 'This file is empty. Please choose a different file.';

const getExtension = (name) => {
  const match = /\.([^./\\]+)$/.exec(String(name ?? ''));
  return match ? match[1].toLowerCase() : '';
};

/** 'image' | 'video' | null, based on the file name's extension. */
export const getMediaKind = (file) => {
  const ext = getExtension(file?.name);
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
  return null;
};

// Magic-byte checks keyed by extension so e.g. a text file renamed to .jpg is
// rejected. `bytes` is the first chunk of the file.
const startsWith = (bytes, sig, offset = 0) => sig.every((b, i) => bytes[offset + i] === b);
const ascii = (s) => [...s].map((c) => c.charCodeAt(0));

const isMp4Family = (b) => startsWith(b, ascii('ftyp'), 4);
const isMatroska = (b) => startsWith(b, [0x1a, 0x45, 0xdf, 0xa3]);

const SIGNATURE_CHECKS = {
  jpg: (b) => startsWith(b, [0xff, 0xd8, 0xff]),
  jpeg: (b) => startsWith(b, [0xff, 0xd8, 0xff]),
  png: (b) => startsWith(b, [0x89, 0x50, 0x4e, 0x47]),
  gif: (b) => startsWith(b, ascii('GIF8')),
  webp: (b) => startsWith(b, ascii('RIFF')) && startsWith(b, ascii('WEBP'), 8),
  heic: isMp4Family, // ISO-BMFF `ftyp` box (brands heic/heix/mif1/...)
  heif: isMp4Family,
  mp4: isMp4Family,
  m4v: isMp4Family,
  mov: (b) => isMp4Family(b) || startsWith(b, ascii('moov'), 4) || startsWith(b, ascii('wide'), 4),
  webm: isMatroska,
  mkv: isMatroska,
  avi: (b) => startsWith(b, ascii('RIFF')) && startsWith(b, ascii('AVI '), 8),
};

const readHeader = (file, length = 16) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(0, length));
  });

/**
 * Validate a device-selected file before it is stored/uploaded: non-empty,
 * supported extension, and content that matches that extension.
 * @returns {Promise<{ valid: boolean, kind: 'image'|'video'|null, error?: string }>}
 */
export const validateMediaFile = async (file) => {
  const kind = getMediaKind(file);
  if (!kind) return { valid: false, kind, error: MEDIA_FILE_ERROR };
  if (!file.size) return { valid: false, kind, error: MEDIA_FILE_EMPTY_ERROR };
  if (file.size > MAX_MEDIA_FILE_SIZE) {
    return { valid: false, kind, error: MEDIA_FILE_TOO_LARGE_ERROR };
  }

  try {
    const header = await readHeader(file);
    if (!SIGNATURE_CHECKS[getExtension(file.name)](header)) {
      return { valid: false, kind, error: MEDIA_FILE_ERROR };
    }
  } catch {
    return { valid: false, kind, error: MEDIA_FILE_ERROR };
  }
  return { valid: true, kind };
};

// Emoji and other 4-byte (astral) characters. The backend can't store these in
// a media name and errors out, so they are dropped. Covers pictographs, ZWJ
// sequences, variation selectors, skin-tone modifiers, flags and keycaps.
const EMOJI_RE = new RegExp(
  [
    '\\p{Extended_Pictographic}',
    '\\p{Emoji_Modifier}',
    '[\\u{1F1E6}-\\u{1F1FF}]', // regional indicators (flags)
    '[\\u{10000}-\\u{10FFFF}]', // any other 4-byte character
    '\\u200D', // zero-width joiner
    '\\uFE0E|\\uFE0F', // variation selectors
    '\\u20E3', // combining keycap
  ].join('|'),
  'gu'
);

/**
 * Make a file name safe to send to the backend by removing emoji, e.g.
 * "party 🎉.png" → "party.png". A name that was only emoji falls back to
 * "media" (keeping its extension). Names without emoji are returned untouched.
 */
export const sanitizeMediaName = (name) => {
  const original = String(name ?? '');
  const stripped = original.replace(EMOJI_RE, '');
  if (stripped === original) {
    return original;
  }
  const tidy = stripped
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+(\.[^.\s]+)$/, '$1') // no stray space before the extension
    .trim();
  return !tidy || tidy.startsWith('.') ? `media${tidy}` : tidy;
};
